<template>
  <div class="queue-mapping-tab">
    <div class="queue-mapping-container">
      <div class="section-header">
        <h3>Queue Mapping</h3>
        <p class="section-description">
          Map ServiceM8 queues to {{ brand.agencyName }} pipeline stages. When a job is assigned to a queue in ServiceM8, it will be mapped to the corresponding pipeline stage in {{ brand.agencyName }}.
        </p>
      </div>
      
      <div v-if="loading" class="loading-state">
        <p>Loading queues and pipelines...</p>
      </div>
      
      <div v-else-if="error" class="error-state">
        <p>{{ error }}</p>
        <button @click="loadData" class="btn-retry">Retry</button>
      </div>
      
      <div v-else class="mapping-table-container">
        <div class="table-header">
          <div class="table-header-left">
            <h4>Queue Mappings</h4>
            <span class="queue-count">{{ filteredQueues.length }} queue(s)</span>
          </div>
          <div class="table-header-right">
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Search queues..." 
              class="search-input"
            />
            <button @click="loadData(true)" class="btn-refresh" :disabled="loading">
              {{ loading ? 'Loading...' : 'Refresh' }}
            </button>
          </div>
        </div>
        
        <div v-if="filteredQueues.length === 0" class="no-data">
          <p>No queues found.</p>
        </div>
        
        <table v-else class="mapping-table">
          <thead>
            <tr>
              <th>ServiceM8 Queue</th>
              <th>Sync Direction</th>
              <th>{{ brand.agencyName }} Pipeline Stage</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="queue in filteredQueues" :key="queue.uuid">
              <td class="queue-cell">
                <div class="queue-info">
                  <span class="queue-name">{{ queue.name }}</span>
                  <span v-if="queue.description" class="queue-description">{{ queue.description }}</span>
                </div>
              </td>
              <td class="sync-direction-cell">
                <span class="sync-arrow" title="Sync flows both ways">↔</span>
              </td>
              <td class="pipeline-cell">
                <select 
                  v-model="queueMappings[queue.uuid]"
                  @change="handlePipelineChange(queue.uuid, $event)"
                  class="pipeline-select"
                >
                  <option value="">Ignore</option>
                  <optgroup 
                    v-for="pipeline in pipelines" 
                    :key="pipeline.id" 
                    :label="pipeline.name"
                    class="pipeline-optgroup"
                  >
                    <option 
                      v-for="stage in pipeline.stages || []" 
                      :key="stage.id" 
                      :value="stage.id"
                    >
                      {{ stage.name }}
                    </option>
                  </optgroup>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { brandMixin } from '../brandMixin'

export default {
  name: 'QueueMappingTab',
  mixins: [brandMixin],
  data() {
    return {
      queues: [],
      pipelines: [],
      queueMappings: {},
      loading: false,
      error: null,
      searchQuery: '',
      cacheKey: 'servicem8_queues_cache',
      cacheExpiry: 7 * 24 * 60 * 60 * 1000, // 7 days in milliseconds
    }
  },
  computed: {
    filteredQueues() {
      if (!this.searchQuery) {
        return this.queues;
      }
      const query = this.searchQuery.toLowerCase();
      return this.queues.filter(queue => 
        queue.name.toLowerCase().includes(query) ||
        (queue.description && queue.description.toLowerCase().includes(query))
      );
    },
  },
  async mounted() {
    await this.loadData(false); // Load with cache by default
  },
  methods: {
    async getSSOToken() {
      return await new Promise((resolve) => {
        window.parent.postMessage({ message: "REQUEST_USER_DATA" }, "*");
        window.addEventListener("message", ({ data }) => {
          if (data.message === "REQUEST_USER_DATA_RESPONSE") {
            resolve(data.payload);
          }
        });
      });
    },
    async loadData(forceRefresh = false) {
      this.loading = true;
      this.error = null;
      
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        
        // Try to load queues from cache first (unless forcing refresh)
        let queueData = null;
        if (!forceRefresh) {
          queueData = this.getCachedQueues();
        }
        
        // Fetch queues if not cached or forcing refresh
        if (!queueData || forceRefresh) {
          const queueRes = await fetch('/servicem8/queues', { headers });
          if (queueRes.ok) {
            queueData = await queueRes.json();
            // Cache the queues
            this.setCachedQueues(queueData);
            console.log('Loaded queues from API and cached');
          } else {
            throw new Error('Failed to load ServiceM8 queues');
          }
        } else {
          console.log('Loaded queues from cache');
        }
        
        // Filter out inactive queues (active=0)
        this.queues = Array.isArray(queueData) 
          ? queueData.filter(q => q.active !== 0 && q.active !== '0')
          : [];
        console.log(`Displaying ${this.queues.length} active queue(s)`);
        
        // Always fetch pipelines fresh (they change more often)
        await this.loadPipelines();
        
        // Load queue mappings from database
        await this.loadQueueMappings();
        
        // Initialize queue mappings for any queues without mappings
        this.queues.forEach(queue => {
          if (!this.queueMappings[queue.uuid]) {
            this.queueMappings[queue.uuid] = '';
          }
        });
      } catch (error) {
        console.error('Error loading queue mapping data:', error);
        this.error = error.message || 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },
    getCachedQueues() {
      try {
        const cached = localStorage.getItem(this.cacheKey);
        if (!cached) return null;
        
        const data = JSON.parse(cached);
        const now = Date.now();
        
        // Check if cache is still valid
        if (data.timestamp && (now - data.timestamp) < this.cacheExpiry) {
          return data.queues;
        } else {
          // Cache expired, remove it
          localStorage.removeItem(this.cacheKey);
          return null;
        }
      } catch (error) {
        console.error('Error reading queue cache:', error);
        return null;
      }
    },
    setCachedQueues(queues) {
      try {
        const cacheData = {
          queues: queues,
          timestamp: Date.now(),
        };
        localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
      } catch (error) {
        console.error('Error caching queues:', error);
      }
    },
    async loadPipelines() {
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        
        const response = await fetch('/servicem8/pipelines', { headers });
        if (response.ok) {
          const data = await response.json();
          this.pipelines = (data.pipelines || []).filter(p => p.stages && p.stages.length > 0);
          console.log(`Loaded ${this.pipelines.length} pipeline(s) with stages`);
          // Log pipeline structure for debugging
          this.pipelines.forEach(p => {
            console.log(`Pipeline "${p.name}" has ${p.stages?.length || 0} stage(s)`);
          });
        } else {
          console.error('Failed to load pipelines');
        }
      } catch (error) {
        console.error('Error loading pipelines:', error);
      }
    },
    async loadQueueMappings() {
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        
        const response = await fetch('/queue-mappings', { headers });
        if (response.ok) {
          const mappings = await response.json();
          this.queueMappings = mappings || {};
          console.log(`Loaded ${Object.keys(this.queueMappings).length} queue mapping(s)`);
        } else {
          console.error('Failed to load queue mappings');
        }
      } catch (error) {
        console.error('Error loading queue mappings:', error);
      }
    },
    async handlePipelineChange(queueUuid, event) {
      const selectedValue = event.target.value;
      
      try {
        if (selectedValue) {
          // Save mapping to database only when a pipeline stage is selected
          this.queueMappings[queueUuid] = selectedValue;
          await this.saveQueueMapping(queueUuid, selectedValue);
          console.log(`Successfully mapped queue ${queueUuid} to pipeline stage ${selectedValue}`);
        } else {
          // Remove mapping from database when changed to "Ignore"
          this.queueMappings[queueUuid] = '';
          await this.deleteQueueMapping(queueUuid);
          console.log(`Successfully removed mapping for queue ${queueUuid}`);
        }
      } catch (error) {
        // Revert the change if save failed
        console.error('Failed to update queue mapping, reverting change:', error);
        // Reload mappings to get the correct state
        await this.loadQueueMappings();
      }
    },
    async saveQueueMapping(queueUuid, pipelineStageId) {
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 
          'x-sso-token': ssoToken,
          'Content-Type': 'application/json',
        };
        
        const response = await fetch('/queue-mappings', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            queueUuid,
            pipelineStageId,
          }),
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(errorData.error || 'Failed to save queue mapping');
        }
        
        const mapping = await response.json();
        console.log('Queue mapping saved:', mapping);
      } catch (error) {
        console.error('Error saving queue mapping:', error);
        throw error;
      }
    },
    async deleteQueueMapping(queueUuid) {
      try {
        const ssoToken = await this.getSSOToken();
        const headers = { 'x-sso-token': ssoToken };
        
        const response = await fetch(`/queue-mappings/${queueUuid}`, {
          method: 'DELETE',
          headers,
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(errorData.error || 'Failed to delete queue mapping');
        }
        
        console.log('Queue mapping deleted');
      } catch (error) {
        console.error('Error deleting queue mapping:', error);
        throw error;
      }
    },
  },
}
</script>

<style scoped>
.queue-mapping-tab {
  width: 100%;
  padding: 20px;
}

.queue-mapping-container {
  max-width: 1200px;
  margin: 0 auto;
}

.section-header {
  margin-bottom: 30px;
}

.section-header h3 {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
}

.section-description {
  color: #666;
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.error-state {
  color: #d32f2f;
}

.btn-retry {
  margin-top: 16px;
  padding: 8px 16px;
  background: #24ACFE;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-retry:hover {
  background: #1a8cd8;
}

.mapping-table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
}

.table-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.table-header-left h4 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.queue-count {
  font-size: 14px;
  color: #666;
  background: #f5f5f5;
  padding: 4px 12px;
  border-radius: 12px;
}

.table-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  width: 250px;
}

.search-input:focus {
  outline: none;
  border-color: #24ACFE;
}

.btn-refresh {
  padding: 8px 16px;
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-refresh:hover:not(:disabled) {
  background: #e0e0e0;
}

.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.no-data {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.mapping-table {
  width: 100%;
  border-collapse: collapse;
}

.mapping-table thead {
  background: #f9f9f9;
}

.mapping-table th {
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #333;
  font-size: 14px;
  border-bottom: 2px solid #e0e0e0;
}

.mapping-table td {
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.mapping-table tbody tr:hover {
  background: #f9f9f9;
}

.queue-cell {
  width: 40%;
}

.sync-direction-cell {
  width: 10%;
  text-align: center;
  padding: 16px 8px !important;
  vertical-align: middle;
}

.sync-arrow {
  font-size: 32px;
  color: #24ACFE;
  font-weight: 900;
  display: inline-block;
  line-height: 1;
  letter-spacing: 2px;
}

.queue-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.queue-name {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.queue-description {
  font-size: 12px;
  color: #666;
}

.pipeline-cell {
  width: 50%;
}

.pipeline-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;
}

.pipeline-select:focus {
  outline: none;
  border-color: #24ACFE;
}

.pipeline-select optgroup {
  font-weight: bold;
  font-size: 14px;
  color: #333;
  background: #f5f5f5;
}

.pipeline-select optgroup option {
  font-weight: normal;
  padding-left: 20px;
  color: #666;
}

@media (max-width: 768px) {
  .table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .table-header-right {
    width: 100%;
  }
  
  .search-input {
    width: 100%;
  }
  
  .mapping-table {
    font-size: 12px;
  }
  
  .mapping-table th,
  .mapping-table td {
    padding: 12px 8px;
  }
}
</style>

